import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, EMPTY, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { loginRoute } from '../../../config/routes.config';
import { addAuthHeader } from '../../../utils/request.utils';
import { RefreshTokenService } from '../../authentication/refresh-token.service';
import { deleteSession } from '../../storage.service';
import { HEADER_ANONYMOUS } from '../headers';

const tokenToBeRefreshed = (error, request: HttpRequest<any>) => (
  error.status === 401
  && !request.headers.has(HEADER_ANONYMOUS)
  && request.url.includes(environment.companyAdmin.apiUrl)
);

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshTokenService: RefreshTokenService;
  private router: Router;
  private refreshTokenInProgress = false;
  private tokenRefreshedSource = new Subject<void>();
  private tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  constructor(private injector: Injector) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    this.loadRefreshTokenService();
    this.loadRouter();

    // Handle response
    return next.handle(request).pipe(
      catchError(
        error => {
          if (tokenToBeRefreshed(error, request)) {
            return this.refreshToken()
              .pipe(
                switchMap(() => {
                  request = addAuthHeader(request);

                  return next.handle(request);
                }),
                catchError(() => {
                  this.logout();

                  return EMPTY;
                })
              );
          }

          return throwError(() => error);
        }
      )
    );
  }

  private refreshToken(): Observable<any> {
    if (this.refreshTokenInProgress) {
      return new Observable<void>(observer => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next(undefined);
          observer.complete();
        });
      });
    } else {
      this.refreshTokenInProgress = true;
      return this.refreshTokenService.refreshToken()
        .pipe(
          tap(() => {
            this.refreshTokenInProgress = false;
            this.tokenRefreshedSource.next(undefined);
          })
        );
    }
  }

  private logout() {
    deleteSession();
    this.router.navigate([loginRoute]);
  }

  private loadRefreshTokenService() {
    if (!this.refreshTokenService) {
      this.refreshTokenService = this.injector.get(RefreshTokenService);
    }
  }

  private loadRouter() {
    if (!this.router) {
      this.router = this.injector.get(Router);
    }
  }
}
